/**
 * UpgradePrompt — Reusable inline component shown when a tier limit is hit (403).
 * Shows what limit was reached, what tier unlocks it, and a CTA to upgrade.
 */

import { useState } from 'react';
import { ArrowUpCircle, Loader2 } from 'lucide-react';
import { createCheckoutSession } from '../lib/api';
import { trackAuthed } from '../lib/analytics';
import { TIERS } from '../lib/tiers';

export default function UpgradePrompt({ limitError, onDismiss }) {
  const [loading, setLoading] = useState(false);

  if (!limitError) return null;

  const upgradeTier = limitError.upgrade_tier;
  const tierInfo = upgradeTier ? TIERS[upgradeTier] : null;

  const handleUpgrade = async () => {
    if (!upgradeTier) return;
    trackAuthed('checkout_click', { tier: upgradeTier, interval: 'monthly', source: 'upgrade_prompt', limit_type: limitError.limit_type });
    setLoading(true);
    try {
      const res = await createCheckoutSession(upgradeTier, 'monthly');
      if (res.checkout_url) {
        window.open(res.checkout_url, '_blank');
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/80 border border-amber-800/40 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <ArrowUpCircle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          <p className="text-sm text-slate-200 font-medium">
            {limitError.error || 'Limit reached'}
          </p>
          {limitError.upgrade_message && (
            <p className="text-xs text-slate-400">{limitError.upgrade_message}</p>
          )}
        </div>
      </div>

      {upgradeTier && tierInfo && (
        <div className="flex items-center gap-2">
          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white text-xs px-4 py-2 rounded-md transition-colors flex items-center gap-1.5"
          >
            {loading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : limitError.requires_trial ? (
              <>Start your free 14-day trial</>
            ) : (
              <>Upgrade to {tierInfo.name} &mdash; ${tierInfo.monthlyPrice}/mo</>
            )}
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-xs text-slate-500 hover:text-slate-300 px-2 py-2"
            >
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}
